// @flow
import {
  CONNECT_TIERED_METRIC_NAME,
  IS_CONNECT_COMMITTED_CAPACITY_LIVE_IN_CE1,
  IS_DSP_STARTER_PACK_LIVE_IN_CE1,
  IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1,
  IS_TABLEFLOW_COMMITTED_CAPACITY_LIVE_IN_CE1,
  TABLEFLOW_TIERED_METRIC_NAME,
  TIERED_PRICING_RATE_CARD,
} from '@src/constants';

import { getEffectiveDiscountForCommittedCapacity } from './utils';

export const ESTIMATE_CC_METRIC_NAMES = {
  ECKU: 'ecku',
  CFU: 'cfu',
  CONNECT: 'connect',
  TABLEFLOW: 'tableflow_topics',
  DSP_STARTER_PACK: 'dsp_starter_pack',
};

export const ESTIMATE_COMMITTED_CAPACITY = {
  ID_BACKEND_FIELD_NAME: 'id',
  METRIC_NAME_BACKEND_FIELD_NAME: 'metricName',
  METRIC_NAME_DISPLAY_NAME: 'Metric Name',
  QUANTITY_BACKEND_FIELD_NAME: 'quantity',
  QUANTITY_DISPLAY_NAME: 'Quantity',
  DISCOUNT_BACKEND_FIELD_NAME: 'discount',
  DISCOUNT_DISPLAY_NAME: '% Discount',
  PROVIDER_BACKEND_FIELD_NAME: 'provider',
  PROVIDER_LABEL_DISPLAY_NAME_COL: 'provider_label',
  PROVIDER_DISPLAY_NAME: 'Cloud Provider',
  CONNECTOR_NAME_BACKEND_FIELD_NAME: 'connector_name',
  CONNECTOR_LABEL_DISPLAY_NAME_COL: 'connector_label',
  CONNECTOR_NAME_DISPLAY_NAME: 'Connector Name',
  CLUSTER_TYPE_BACKEND_FIELD_NAME: 'cluster_type',
  CLUSTER_TYPE_LABEL_DISPLAY_NAME_COL: 'cluster_type_label',
  CLUSTER_TYPE_DISPLAY_NAME: 'Cluster SKU',
  IS_DENSER_BIN_PACKING_BACKEND_FIELD_NAME: 'is_denser_bin_packing_enabled',
  IS_DENSER_BIN_PACKING_DISPLAY_NAME: 'Denser Bin Packing',
  IS_ADMIN_EDITED_BACKEND_FIELD_NAME: 'is_admin_edited',
  METRICS_LEVEL_CONFIGURATION_ARRAY: 'committed_capacity_inputs',
  METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY:
    'committed_capacity_metrics_dimensions_level_configuration_array',
  // Placeholder of the DSP Starter Pack 'quantity' input; keep in sync with its nonDimensionFieldLabels entry.
  DSP_SP_COMMITMENT_AMOUNT_DISPLAY_NAME: 'Commitment Amount',
  ELIGIBILITY_DATA_BACKEND_FIELD_NAME: 'committed_capacity_eligibility',
  COMPLETE_METRICS_LIST: [
    {
      name: 'ecku',
      label: 'eCKU',
      dimensions: ['provider', 'cluster_type'],
      dimensionLabels: ['Cloud Provider', 'Cluster SKU'],
      dimensionDefaults: [null, null],
      nonDimensionFields: ['quantity'],
      nonDimensionFieldLabels: ['Quantity'],
      nonDimensionFieldsDefaults: [null],
      minQuantityForAdmins: 0,
      minQuantityForNonAdmins: 10,
      isLive: IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1,
      shouldShowDiscountField: false,
      hideForRenewals: false,
    },
    {
      name: 'cfu',
      label: 'CFU',
      dimensions: ['provider'],
      dimensionLabels: ['Cloud Provider'],
      dimensionDefaults: [null, null],
      nonDimensionFields: ['quantity'],
      nonDimensionFieldLabels: ['Quantity'],
      nonDimensionFieldsDefaults: [null],
      minQuantityForAdmins: 0,
      minQuantityForNonAdmins: 25,
      isLive: IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1,
      shouldShowDiscountField: false,
      hideForRenewals: false,
      mutuallyExclusiveGroup: 'dsp-elems',
      disabledWhenCCStarterCommitActive: true,
    },
    {
      name: ESTIMATE_CC_METRIC_NAMES.CONNECT,
      label: 'Connect',
      dimensions: ['provider', 'connector_name'],
      dimensionLabels: ['Cloud Provider', 'Connector Name'],
      dimensionDefaults: [null, null],
      nonDimensionFields: ['is_denser_bin_packing_enabled', 'quantity', 'discount'],
      nonDimensionFieldLabels: ['Denser Bin Packing', 'Quantity', '% Discount'],
      nonDimensionFieldsDefaults: [false, null, null],
      adminOnlyDimensionFields: ['is_denser_bin_packing_enabled'],
      minQuantityForAdmins: 0,
      minQuantityForNonAdmins: 200,
      isLive: IS_CONNECT_COMMITTED_CAPACITY_LIVE_IN_CE1,
      shouldShowDiscountField: true,
      hideForRenewals: false,
      mutuallyExclusiveGroup: 'dsp-elems',
      disabledWhenCCStarterCommitActive: true,
      applicableRateCards: [TIERED_PRICING_RATE_CARD],
      // Note: If this key is not provided, then, it means, there is no restriction on RCs
      // The backend metric name used for tiered pricing lookups.
      // This is the key used to filter MLD (Metric Level Discount) entries from the backend.
      // Example: 'ConnectNumTasksTiered' is the metric name the backend uses for Connect tiered pricing.
      tieredMetricName: CONNECT_TIERED_METRIC_NAME,

      // Extracts the MLD dimension values in the exact order the backend sends them.
      // The backend stores MLD dimensions in a specific order for each metric type,
      // and this function must match that order for dimension subset matching to work.
      // For Connect, the backend order is: [provider, tier, connector_type]
      // Example: ('aws', 'tier_1') => ['aws', 'tier_1', 's3sink']
      //          ('gcp', 'tier_2') => ['gcp', 'tier_2', 'bigquerysink']
      tieredMetricMLDDimsExtractorFunc: (row, tierKey) => [
        row.provider,
        tierKey,
        row.connector_name,
      ],

      // Computes the permitted discount range (minDiscount, maxDiscount) for non-admin users.
      // Returns { minDiscount, maxDiscount } if a range applies, or null if no restriction.
      // For Connect (always on tiered RC): calculates the effective tiered discount, then
      // the allowed range is MAX(50%, effectiveDiscount) to effectiveDiscount + 5% (capped at 100%).
      // Example: If effective discount = 47%, range is { minDiscount: 50, maxDiscount: 55 }
      //          If effective discount = 62%, range is { minDiscount: 62, maxDiscount: 67 }
      discountMinMaxRangeExtractorFunc: ({
        tieredConfig,
        globalDiscount,
        mldMetrics,
        row,
        quantity,
      }) => {
        // If the row isn't fully populated, skip discount range validation.
        if (!row.provider || !row.connector_name) {
          return null;
        }

        const tiersConfig = tieredConfig?.connect?.tiers;
        if (!tiersConfig) {
          return null;
        }
        const effectiveDiscount = getEffectiveDiscountForCommittedCapacity(
          tiersConfig,
          globalDiscount,
          mldMetrics,
          row,
          ESTIMATE_CC_METRIC_NAMES.CONNECT,
          quantity
        );
        const minDiscount = Math.max(50, effectiveDiscount);
        const maxDiscount = Math.min(minDiscount + 5, 100);
        return { minDiscount, maxDiscount };
      },
    },
    {
      name: ESTIMATE_CC_METRIC_NAMES.TABLEFLOW,
      label: 'Tableflow Topics',
      dimensions: ['provider'],
      dimensionLabels: ['Cloud Provider'],
      dimensionDefaults: [null],
      nonDimensionFields: ['quantity', 'discount'],
      nonDimensionFieldLabels: ['Quantity', '% Discount'],
      nonDimensionFieldsDefaults: [null, null],
      minQuantityForAdmins: 0,
      minQuantityForNonAdmins: 100,
      isLive: IS_TABLEFLOW_COMMITTED_CAPACITY_LIVE_IN_CE1,
      shouldShowDiscountField: true,
      hideForRenewals: false,
      mutuallyExclusiveGroup: 'dsp-elems',
      disabledWhenCCStarterCommitActive: true,
      // The backend metric name used for tiered pricing lookups.
      // Example: 'TableflowNumTasksTiered' is the metric name the backend uses for Tableflow tiered pricing.
      tieredMetricName: TABLEFLOW_TIERED_METRIC_NAME,

      // Extracts the MLD dimension values in the exact order the backend sends them.
      // For Tableflow, the backend order is: [provider, tier] (no extra dimensions unlike Connect).
      // Example: ('aws', 'tier_1') => ['aws', 'tier_1']
      //          ('gcp', 'tier_3') => ['gcp', 'tier_3']
      tieredMetricMLDDimsExtractorFunc: (row, tierKey) => [row.provider, tierKey],

      // Computes the permitted discount range (minDiscount, maxDiscount) for non-admin users.
      // Returns { minDiscount, maxDiscount } if a range applies, or null if no restriction.
      // Tableflow has two modes depending on the rate card:
      //   - Non-tiered RC (old rate cards): fixed range of 50% to 55%, regardless of effective discount.
      //   - Tiered RC: same logic as Connect — MAX(50%, effectiveDiscount) to effectiveDiscount + 5%.
      // Example (non-tiered): Always { minDiscount: 50, maxDiscount: 55 }
      // Example (tiered, effective = 53%): { minDiscount: 53, maxDiscount: 58 }
      discountMinMaxRangeExtractorFunc: ({
        rateCard,
        tieredConfig,
        globalDiscount,
        mldMetrics,
        row,
        quantity,
      }) => {
        // If the row isn't fully populated, skip discount range validation.
        if (!row.provider) {
          return null;
        }

        if (rateCard !== TIERED_PRICING_RATE_CARD) {
          return { minDiscount: 50, maxDiscount: 55 };
        }
        const tiersConfig = tieredConfig?.tableflow?.tiers;
        if (!tiersConfig) {
          return null;
        }
        const effectiveDiscount = getEffectiveDiscountForCommittedCapacity(
          tiersConfig,
          globalDiscount,
          mldMetrics,
          row,
          ESTIMATE_CC_METRIC_NAMES.TABLEFLOW,
          quantity
        );
        const minDiscount = Math.max(50, effectiveDiscount);
        const maxDiscount = Math.min(minDiscount + 5, 100);
        return { minDiscount, maxDiscount };
      },
    },
    {
      name: ESTIMATE_CC_METRIC_NAMES.DSP_STARTER_PACK,
      label: 'Streamhouse Accelerator',
      dimensions: [],
      dimensionLabels: [],
      dimensionDefaults: [],
      nonDimensionFields: ['quantity', 'discount'],
      nonDimensionFieldLabels: ['Commitment Amount', '% Discount'],
      nonDimensionFieldsDefaults: [25000, 50],
      minQuantityForAdmins: 0,
      minQuantityForNonAdmins: 0,
      // 'quantity' holds the Commitment Amount ($) for this metric. Non-admins may set it
      // themselves up to this cap; anything above needs an admin.
      maxQuantityForNonAdmins: 50000,
      isLive: IS_DSP_STARTER_PACK_LIVE_IN_CE1,
      shouldShowDiscountField: true,
      // % Discount is pre-approved at the default above; only admins may change it.
      isDiscountEditableByNonAdmins: false,
      hideForRenewals: true,
      mutuallyExclusiveGroup: 'dsp-starter-pack',
      applicableRateCards: [TIERED_PRICING_RATE_CARD],
      canAddMultipleRows: false,
    },
  ],
};
